// Client-side wrapper around the peaks worker. Opens one lazy worker,
// tracks pending requests by id, returns promises the pad awaits.
//
// Falls back to synchronous computePeaks when Workers aren't available
// (jsdom tests, SSR). The fallback is slow on long buffers but keeps
// every caller site the same.

import { computePeaks, type ComputePeaksResponse } from './peaks';

type Resolve = (res: ComputePeaksResponse) => void;

let worker: Worker | null = null;
let nextId = 1;
const pending = new Map<number, Resolve>();

function supportsWorkers(): boolean {
  return typeof Worker !== 'undefined' && typeof window !== 'undefined';
}

function getWorker(): Worker | null {
  if (!supportsWorkers()) return null;
  if (worker) return worker;
  try {
    worker = new Worker(new URL('./peaks.worker.ts', import.meta.url), {
      type: 'module',
    });
    worker.addEventListener('message', (e: MessageEvent<ComputePeaksResponse>) => {
      const { id } = e.data;
      const resolve = pending.get(id);
      if (resolve) {
        pending.delete(id);
        resolve(e.data);
      }
    });
    worker.addEventListener('error', (err) => {
      console.error('[peaks worker]', err);
      for (const [, resolve] of pending) {
        resolve({ id: -1, perChannel: [], buckets: 0 });
      }
      pending.clear();
    });
    return worker;
  } catch (e) {
    console.warn('[peaks worker] unavailable, using sync fallback', e);
    return null;
  }
}

export function requestPeaks(
  channels: Float32Array[],
  buckets: number,
): Promise<ComputePeaksResponse> {
  const w = getWorker();
  if (!w) {
    const env = computePeaks(channels, buckets);
    return Promise.resolve({ id: 0, perChannel: env.perChannel, buckets: env.buckets });
  }
  const id = nextId++;
  return new Promise((resolve) => {
    pending.set(id, resolve);
    w.postMessage({ id, channels, buckets });
  });
}
